import { Card } from '@/components/ui';
import type { BunkerPriceHistory, HistoricalPeriod } from '@/types/bunkerPrices.types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format, getISOWeek } from 'date-fns';

interface BunkerPriceChartProps {
  history: BunkerPriceHistory[];
  loading: boolean;
  selectedPeriod: HistoricalPeriod;
}

function formatChartDateLabel(dateValue: string, period: HistoricalPeriod): string {
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return dateValue;

  switch (period) {
    case 0:
      return format(parsedDate, 'dd/MM/yyyy');
    case 1:
      return `Week ${getISOWeek(parsedDate)}`;
    case 2:
      return format(parsedDate, 'MMM yyyy');
    case 3:
      return format(parsedDate, 'yyyy');
    default:
      return dateValue;
  }
}

export function BunkerPriceChart({ history, loading, selectedPeriod }: BunkerPriceChartProps) {
  const formatPrice = (price: number) => `$${price.toFixed(2)}`;

  // Colors for different fuel types (matching card colors)
  const fuelColors: Record<string, string> = {
    HFO: '#0ea5e9', // primary-500
    VLSFO: '#475569', // dark-600
    GO: '#f59e0b', // amber-500
  };

  const sortedHistory = [...history].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const uniqueDates = Array.from(new Set(sortedHistory.map((item) => item.date)));
  const fuelTypes = Array.from(new Set(sortedHistory.map((item) => item.typeName)));
  const valueByDateAndType = new Map<string, number>();

  sortedHistory.forEach((item) => {
    valueByDateAndType.set(`${item.date}|${item.typeName}`, item.price);
  });

  const chartData = uniqueDates.map((dateValue) => {
    const row: Record<string, string | number | null> = {
      date: formatChartDateLabel(dateValue, selectedPeriod),
      fullDate: dateValue,
    };

    fuelTypes.forEach((fuelType) => {
      row[fuelType] = valueByDateAndType.get(`${dateValue}|${fuelType}`) ?? null;
    });

    return row;
  });

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ dataKey: string; value: number; color: string; name: string }>;
    label?: string;
  }) => {
    if (active && payload && payload.length) {
      return (
        <div className="max-w-xs rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <p className="mb-2 text-sm font-medium text-gray-900 dark:text-white">{label}</p>
          {payload.map((entry, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatPrice(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({
    payload,
  }: {
    payload?: Array<{ value?: string | number; color?: string }>;
  }) => {
    if (!payload?.length) return null;

    return (
      <div className="mb-2 flex items-center justify-center gap-4 text-xs sm:gap-5 sm:text-sm">
        {payload.map((entry, index) => (
          <div key={`${entry.value}-${index}`} className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-10 border-2 border-solid"
              style={{ borderColor: entry.color ?? '#6B7280' }}
            />
            <span className="text-gray-700 dark:text-gray-300">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Card noPadding className="p-3 sm:p-4">
        <div className="flex h-64 items-center justify-center sm:h-80">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
        </div>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card noPadding className="p-3 sm:p-4">
        <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400 sm:h-80">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="mt-2 text-sm sm:text-base">No chart data available</p>
            <p className="text-xs sm:text-sm">Select a port to view historical data</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card noPadding className="p-3 sm:p-4">
      <div className="h-80 w-full sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              className="text-gray-600 dark:text-gray-300"
              height={32}
              tickMargin={8}
              minTickGap={24}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              className="text-gray-600 dark:text-gray-300"
              tickFormatter={(value) => formatPrice(value)}
              width={80}
              domain={['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="top" align="center" content={<CustomLegend />} />
            {fuelTypes.map((fuelType) => (
              <Line
                key={fuelType}
                type="monotone"
                dataKey={fuelType}
                stroke={fuelColors[fuelType] || '#64748b'}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls={true}
                name={fuelType}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
